import React, { useCallback, useEffect, useRef, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import api, { apiError } from "../lib/api";
import { Button } from "../components/ui/button";
import { Textarea } from "../components/ui/textarea";
import { Checkbox } from "../components/ui/checkbox";
import { StatusBadge } from "../components/StatusBadge";
import { ArrowLeft, CheckCircle2, Copy, Download, Lock, Sparkles, Video, AlertTriangle, Loader2 } from "lucide-react";
import { toast } from "sonner";

export default function SessionEditor() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [s, setS] = useState(null);
  const [note, setNote] = useState("");
  const noteRef = useRef(null);
  const [version, setVersion] = useState(1);
  const [saving, setSaving] = useState("");
  const [attest, setAttest] = useState(false);
  const [final, setFinal] = useState(null);
  const [busy, setBusy] = useState(false);
  const timer = useRef(null);

  const [secs, setSecs] = useState([]);
  const load = useCallback(async () => {
    try { const { data } = await api.get(`/sessions/${id}`); setS(data); setVersion(data.version); const ns = data.payload?.note_sections || []; setSecs(ns); setNote(ns.map((x) => x.body || "").filter(Boolean).join("\n\n")); }
    catch (e) { toast.error(apiError(e)); }
  }, [id]);
  useEffect(() => { load(); }, [load]);

  const save = useCallback(async (sections, ver) => {
    setSaving("Saving…");
    try { const { data } = await api.put(`/sessions/${id}/note`, { version: ver, note_sections: sections }); setVersion(data.version); setSaving("Saved"); }
    catch (e) { setSaving("Save failed"); if (e?.response?.status === 409) { toast.error("Changed elsewhere — reloading"); load(); } }
  }, [id, load]);

  const multi = secs.length > 1;
  const edit = (body) => {
    setNote(body);
    const sections = [{ heading: secs[0]?.heading || "Clinical Note", body }];
    setSecs(sections);
    clearTimeout(timer.current);
    timer.current = setTimeout(() => save(sections, version), 1200);
  };
  const [active, setActive] = useState(0);
  const editSec = (i, body) => {
    const sections = secs.map((x, k) => (k === i ? { ...x, body } : x));
    setSecs(sections); setNote(sections.map((x) => x.body || "").filter(Boolean).join("\n\n"));
    clearTimeout(timer.current);
    timer.current = setTimeout(() => save(sections, version), 1200);
  };
  const insertPoint = (point) => {
    if (multi) { const cur = secs[active]?.body || ""; editSec(active, `${cur}${cur && !cur.endsWith("\n") ? "\n" : ""}${point}\n`); return; }
    const el = noteRef.current;
    const pos = el && document.activeElement === el ? el.selectionStart : note.length;
    const before = note.slice(0, pos), after = note.slice(pos);
    const sep = before && !before.endsWith("\n") ? "\n" : "";
    edit(`${before}${sep}${point}${after.startsWith("\n") || !after ? "" : "\n"}${after}`);
    el?.focus();
  };
  const copyNote = async () => { try { await navigator.clipboard.writeText(multi ? secs.map((x) => `${x.heading}\n${x.body || ""}`).join("\n\n") : note); toast.success("Note copied"); } catch { toast.error("Copy failed"); } };

  const submitFinal = async () => {
    setBusy(true);
    clearTimeout(timer.current);
    await save(secs, version);
    try { const { data } = await api.post(`/sessions/${id}/final-submit`, { attestation: attest }); setFinal(data); }
    catch (e) { toast.error(apiError(e)); }
    setBusy(false);
  };
  const dlFinal = () => { const a = document.createElement("a"); a.href = `data:application/pdf;base64,${final.pdf_base64}`; a.download = final.filename; a.click(); };
  const dlDraft = async () => {
    const r = await api.get(`/sessions/${id}/draft-pdf`, { responseType: "blob" });
    const a = document.createElement("a"); a.href = URL.createObjectURL(r.data); a.download = `${s.client_name}_${s.document_type} DRAFT.pdf`; a.click();
  };

  if (!s) return <div className="p-8 text-muted-foreground">Loading…</div>;
  const pl = s.payload || {};
  const points = pl.session_points;
  const isFinal = s.status === "completed" || s.status === "delivery_failed";

  if (final) return (
    <div className="mx-auto max-w-xl space-y-5 text-center" data-testid="final-success">
      <CheckCircle2 className="mx-auto h-16 w-16 text-secondary" />
      <h1 className="font-head text-3xl font-extrabold">Submitted final</h1>
      <p className="break-all font-mono text-sm font-bold">{final.tracking_id}</p>
      <div className="flex justify-center"><StatusBadge status={final.delivery_status} /></div>
      <p className="text-sm text-muted-foreground">The PDF was emailed to your agency's clinical documents inbox. Download it now to upload to your EMR.</p>
      <div className="flex justify-center gap-3">
        <Button className="rounded-full" onClick={dlFinal} data-testid="download-final"><Download className="mr-2 h-4 w-4" /> Download PDF</Button>
        <Button variant="outline" className="rounded-full" onClick={() => navigate("/app/sessions")}>Back to sessions</Button>
      </div>
    </div>
  );

  return (
    <div className="mx-auto max-w-6xl space-y-6">
      <button onClick={() => navigate("/app/sessions")} className="flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground"><ArrowLeft className="h-4 w-4" /> Sessions</button>
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="font-head text-2xl font-extrabold tracking-tight">{s.document_type}</h1>
          <p className="text-sm text-muted-foreground">{s.client_name} · DOB {s.dob} · <span className="font-mono text-xs">{s.tracking_id}</span></p>
        </div>
        <div className="flex items-center gap-2">
          <span data-testid="save-indicator" className="text-xs text-muted-foreground">{saving}</span>
          <StatusBadge status={s.status} />
          {s.status === "in_session" && <Button size="sm" className="rounded-full" onClick={() => navigate(`/app/sessions/${id}/room`)}><Video className="mr-1 h-4 w-4" /> Return to room</Button>}
        </div>
      </div>

      <div className="grid gap-6 lg:grid-cols-[1fr_320px]">
        <div className="space-y-4">
          <div className="rounded-2xl border border-border bg-card p-5" data-testid="note-block">
            <div className="mb-2 flex items-center justify-between gap-2">
              <h2 className="font-head font-bold">{s.document_type}</h2>
              <Button type="button" variant="ghost" size="sm" className="h-8 rounded-full" onClick={copyNote} disabled={!note} data-testid="copy-note"><Copy className="mr-1 h-3.5 w-3.5" /> Copy</Button>
            </div>
            {multi ? (
              <div>
                <div className="mb-3 flex flex-wrap gap-1.5" role="tablist" data-testid="note-tabs">{secs.map((x, i) => <button key={i} role="tab" aria-selected={active === i} onClick={() => setActive(i)} data-testid={`note-tab-${i}`} className={`rounded-full border px-3 py-1 text-xs font-semibold ${active === i ? "border-primary bg-primary text-white" : (x.body || "").trim() ? "border-primary/40 bg-primary/5 text-primary" : "border-border text-muted-foreground"}`}>{x.heading}</button>)}</div>
                <Textarea rows={14} value={secs[active]?.body || ""} disabled={isFinal} onChange={(e) => editSec(active, e.target.value)} data-testid="note-body"
                  className="min-h-[320px] resize-y text-sm leading-relaxed" placeholder={`Write the "${secs[active]?.heading}" section. Use the AI points on the right to insert into this section.`} />
                <p className="mt-2 text-[11px] text-muted-foreground">Sections come from your agency's template for {s.document_type}. Autosaves as you type; filled sections are highlighted.</p>
              </div>
            ) : (<>
            <Textarea ref={noteRef} rows={22} value={note} disabled={isFinal} onChange={(e) => edit(e.target.value)} data-testid="note-body"
              className="min-h-[480px] resize-y font-mono text-sm leading-relaxed"
              placeholder="Write your note in your own format. Type freely or paste from another document — headings, bullets, and spacing are kept exactly as you enter them." />
            <p className="mt-2 text-[11px] text-muted-foreground">Free-form. Autosaves as you type. Printed to the PDF exactly as written.</p>
            </>)}
          </div>

          {!isFinal && (
            <div className="rounded-2xl border-2 border-primary/20 bg-primary/5 p-5">
              <h2 className="font-head text-lg font-bold">Submit final</h2>
              <p className="mt-1 text-sm text-muted-foreground">Generates the clean, branded PDF, emails it to your agency's inbox, and gives you an instant download. Not editable afterward.</p>
              {!s.evidence_locked && <p className="mt-2 flex items-center gap-1 text-xs text-accent"><AlertTriangle className="h-3 w-3" /> End the session (initial submit) first so evidence is locked.</p>}
              <label className="mt-3 flex items-start gap-2 text-sm"><Checkbox checked={attest} onCheckedChange={setAttest} data-testid="attestation-checkbox" className="mt-0.5" /><span>I attest that this documentation reflects the session to the best of my ability and knowledge, is accurate and complete, and I accept professional responsibility for its content, including any AI-assisted points I chose to include.</span></label>
              <div className="mt-4 flex flex-wrap gap-2">
                <Button className="h-11 rounded-full" disabled={!attest || !s.evidence_locked || busy} onClick={submitFinal} data-testid="submit-final">{busy ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null} Submit final</Button>
                <Button variant="outline" className="h-11 rounded-full" onClick={dlDraft} data-testid="download-draft-pdf"><Download className="mr-2 h-4 w-4" /> Download DRAFT PDF</Button>
              </div>
            </div>
          )}
        </div>

        <aside className="space-y-4">
          <div className="rounded-2xl border border-border bg-card p-4">
            <p className="flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wider text-muted-foreground"><Lock className="h-3.5 w-3.5" /> Locked evidence</p>
            {pl.photo ? <img src={pl.photo} alt="Client" className="mt-3 h-32 w-full rounded-xl object-cover" data-testid="locked-photo" /> : <p className="mt-2 text-xs text-muted-foreground">No photo captured.</p>}
            <div className="mt-3 space-y-2">
              {(pl.signatures || []).map((g, i) => (
                <div key={i} className="flex items-center gap-2 rounded-xl border border-border p-2" data-testid={`locked-signature-${i}`}>
                  <img src={g.image} alt="" className="h-9 w-16 object-contain" />
                  <div className="min-w-0"><p className="truncate text-xs font-semibold">{g.name}{g.credentials && `, ${g.credentials}`}</p><p className="truncate text-[11px] text-muted-foreground">{g.role}</p></div>
                </div>
              ))}
            </div>
          </div>

          <div className="rounded-2xl border border-border bg-card p-4" data-testid="session-points-panel">
            <p className="flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wider text-muted-foreground"><Sparkles className="h-3.5 w-3.5 text-accent" /> AI session points</p>
            <p className="mt-1 text-[11px] text-muted-foreground">AI-generated guide — review before use. Tap a point to insert it. Never included in the PDF unless you write it in.</p>
            {!points || points.error || !points.sections?.length
              ? <p className="mt-3 text-xs text-muted-foreground">{points?.error ? "Session points unavailable." : "No session points available (listening was not enabled or nothing was captured)."}</p>
              : (
                <div className="mt-3 space-y-3">
                  {points.risk_flags?.length > 0 && <div className="rounded-xl border border-rose-200 bg-rose-50 p-2 text-xs text-rose-800"><b>Risk flags:</b> {points.risk_flags.join("; ")}</div>}
                  {points.sections.map((sec) => (
                    <div key={sec.heading}>
                      <p className="text-xs font-bold">{sec.heading}</p>
                      <ul className="mt-1 space-y-1">
                        {sec.points.map((p, k) => (
                          <li key={k}><button disabled={isFinal} onClick={() => insertPoint(p)} className="w-full rounded-lg bg-muted/60 px-2 py-1.5 text-left text-xs leading-snug hover:bg-muted disabled:opacity-60" data-testid="session-point">{p}</button></li>
                        ))}
                      </ul>
                    </div>
                  ))}
                </div>
              )}
          </div>
        </aside>
      </div>
    </div>
  );
}
